import apiClient from "../apiClient";

const backgroundVerificationService = {
  // Get all background verifications with filtering
  getBackgroundVerifications: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.status) queryParams.append('status', params.status);
      if (params.vendor) queryParams.append('vendor', params.vendor);
      if (params.type) queryParams.append('type', params.type);
      if (params.search) queryParams.append('search', params.search);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);

      const url = `/background-verification${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

      const response = await apiClient.get(url);

      // Ensure consistent response format
      if (response.data && response.data.responseCode) {
        return response.data;
      } else {
        // Wrap direct response in standard format
        return {
          responseCode: 200,
          responseMessage: "Background verifications retrieved successfully",
          data: response.data,
        };
      }
    } catch (error) {
      return {
        success: false,
        responseCode: error.response?.status || 500,
        responseMessage:
          error.response?.data?.responseMessage ||
          error.message ||
          "Failed to fetch background verifications",
        error: error.response?.data?.error || error.message,
        data: null,
      };
    }
  },

  // Get eligible candidates for background verification
  getEligibleCandidates: async () => {
    try {
      const response = await apiClient.get('/background-verification/eligible-candidates');

      // Ensure consistent response format
      if (response.data && response.data.responseCode) {
        return response.data;
      } else {
        // Wrap direct response in standard format
        return {
          responseCode: 200,
          responseMessage: 'Eligible candidates retrieved successfully',
          data: response.data
        };
      }
    } catch (error) {
      return {
        success: false,
        responseCode: error.response?.status || 500,
        responseMessage: error.response?.data?.responseMessage || error.message || 'Failed to fetch eligible candidates',
        error: error.response?.data?.error || error.message,
        data: null
      };
    }
  },

  // Get background verification by ID
  getBackgroundVerificationById: async (id) => {
    try {
      const response = await apiClient.get(`/background-verification/${id}`);

      // Ensure consistent response format
      if (response.data && response.data.responseCode) {
        return response.data;
      } else {
        // Wrap direct response in standard format
        return {
          responseCode: 200,
          responseMessage: "Background verification retrieved successfully",
          data: response.data,
        };
      }
    } catch (error) {
      return {
        success: false,
        responseCode: error.response?.status || 500,
        responseMessage:
          error.response?.data?.responseMessage ||
          error.message ||
          "Failed to fetch background verification",
        error: error.response?.data?.error || error.message,
        data: null,
      };
    }
  },

  // Create new background verification
  createBackgroundVerification: async (verificationData) => {
    try {
      const response = await apiClient.post(
        "/background-verification",
        verificationData
      );

      return response.data;
    } catch (error) {
      return {
        success: false,
        responseCode: error.response?.status || 500,
        responseMessage:
          error.response?.data?.responseMessage ||
          error.message ||
          "Failed to create background verification",
        error: error.response?.data?.error || error.message,
        data: null,
      };
    }
  },

  // Update background verification
  updateBackgroundVerification: async (id, verificationData) => {
    try {
      const response = await apiClient.put(
        `/background-verification/${id}`,
        verificationData
      );

      // The apiClient interceptor already returns response.data, so response is already the data
      return response;
    } catch (error) {
      return {
        success: false,
        responseCode: error.response?.status || 500,
        responseMessage:
          error.response?.data?.responseMessage ||
          error.message ||
          "Failed to update background verification",
        error: error.response?.data?.error || error.message,
        data: null,
      };
    }
  },

  // Submit vendor update
  submitVendorUpdate: async (id, updateData) => {
    try {
      const response = await apiClient.post(
        `/background-verification/${id}/vendor-update`,
        updateData
      );

      return response.data;
    } catch (error) {
      return {
        success: false,
        responseCode: error.response?.status || 500,
        responseMessage:
          error.response?.data?.responseMessage ||
          error.message ||
          "Failed to submit vendor update",
        error: error.response?.data?.error || error.message,
        data: null,
      };
    }
  },

  // Submit HR decision
  submitHrDecision: async (id, decisionData) => {
    try {
      const response = await apiClient.post(
        `/background-verification/${id}/hr-decision`,
        decisionData
      );

      return response.data;
    } catch (error) {
      return {
        success: false,
        responseCode: error.response?.status || 500,
        responseMessage:
          error.response?.data?.responseMessage ||
          error.message ||
          "Failed to submit HR decision",
        error: error.response?.data?.error || error.message,
        data: null,
      };
    }
  },

  // Delete background verification
  deleteBackgroundVerification: async (id) => {
    try {
      const response = await apiClient.delete(`/background-verification/${id}`);

      // The apiClient interceptor returns response.data directly, so response is already the data
      // Ensure we always return a proper response object
      if (response && (response.responseCode === 200 || response.responseCode === 201 || response.success === true)) {
        return response;
      } else if (response && response.data) {
        // If response has nested data, return it
        return response.data;
      } else {
        // Create success response if structure is different
        return {
          success: true,
          responseCode: 200,
          responseMessage: "Background verification deleted successfully",
          data: response || null,
        };
      }
    } catch (error) {
      return {
        success: false,
        responseCode: error.response?.status || 500,
        responseMessage:
          error.response?.data?.responseMessage ||
          error.message ||
          "Failed to delete background verification",
        error: error.response?.data?.error || error.message,
        data: null,
      };
    }
  },

  // Send reminder to vendor
  sendReminder: async (id, message = null) => {
    try {
      const response = await apiClient.post(`/background-verification/${id}/send-reminder`, {
        message: message
      });

      return response;
    } catch (error) {
      return {
        success: false,
        responseCode: error.response?.status || 500,
        responseMessage: error.response?.data?.responseMessage || error.message || 'Failed to send reminder',
        error: error.response?.data?.error || error.message,
        data: null
      };
    }
  },

  // Refresh verification status
  refreshStatus: async (id) => {
    try {
      const response = await apiClient.post(`/background-verification/${id}/refresh-status`);

      return response;
    } catch (error) {
      return {
        success: false,
        responseCode: error.response?.status || 500,
        responseMessage: error.response?.data?.responseMessage || error.message || 'Failed to refresh status',
        error: error.response?.data?.error || error.message,
        data: null
      };
    }
  },

  // Download verification report
  downloadReport: async (id, format = 'pdf') => {
    try {
      // For JSON responses, don't use blob responseType
      const response = await apiClient.get(`/background-verification/${id}/download-report?format=${format}`);

      return response;
    } catch (error) {
      return {
        success: false,
        responseCode: error.response?.status || 500,
        responseMessage: error.response?.data?.responseMessage || error.message || 'Failed to download report',
        error: error.response?.data?.error || error.message,
        data: null
      };
    }
  },

  // Send vendor notification email for new verification
  sendVendorNotification: async (verificationId, vendorEmail, vendorName, candidateName, verificationType, expectedCompletionDate, priority, status) => {
    try {
      const response = await apiClient.post(`/background-verification/${verificationId}/send-vendor-notification`, {
        vendorEmail,
        vendorName,
        candidateName,
        verificationType,
        expectedCompletionDate,
        priority,
        status
      });

      return response;
    } catch (error) {
      return {
        success: false,
        responseCode: error.response?.status || 500,
        responseMessage: error.response?.data?.responseMessage || error.message || 'Failed to send vendor notification',
        error: error.response?.data?.error || error.message,
        data: null
      };
    }
  }
};

export default backgroundVerificationService;
